import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Switch,
  FormControlLabel,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  Grid,
} from "@mui/material";
import {
  Sync as SyncIcon,
  Inventory as InventoryIcon,
  Store as StoreIcon,
  TrendingUp as StockIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
  MenuBook as DocsIcon,
  Close as CloseIcon,
  PlayArrow as RunIcon,
} from "@mui/icons-material";
import { Title, useNotify } from "react-admin";
import config from "../config";
import Logger from "../utils/Logger";
import { getValidToken } from "../services/tokenRefreshService";
import StoreOnboardingDialog from "./StoreLocation/StoreOnboardingDialog";

/**
 * SyncControl Component - Manual triggers for system sync operations
 */
/**
 * Sync Control — split by which retailer's catalogue the action touches.
 *
 * Each retailer's Vend actions live on its own page (Jason, 31 Oct 2026):
 *
 * variant="deeliver"  (/deeliver/sync) — "Deeliver Sync".
 *   Full Inventory Sync - Deeliver (its own 2AM cron) and Stock Sync - Deeliver.
 *
 * variant="vaperite"  (/vaperite/sync) — "Vaperite Sync".
 *   Vaperite's scheduled catalogue sync and its controls (on/off, interval, Run now),
 *   then Stock Sync - Vaperite. The schedule (`vaperite-vend`) always pulls Vaperite's
 *   Lightspeed into Item Staging; its account is fixed server-side.
 *
 * variant="all"  (/admin-functions/sync-control) — "Sync Control".
 *   App-wide only: Stores from Radar and Refresh Views - Entire App.
 */
type Org = 'deeliver' | 'vaperite';

interface SyncControlProps {
  variant?: 'all' | Org;
}

const SyncControl: React.FC<SyncControlProps> = ({ variant = 'all' }) => {
  const showAll = variant === 'all';
  const isDeeliver = variant === 'deeliver';
  const isVaperite = variant === 'vaperite';
  const showDocsButton = !isVaperite;
  const notify = useNotify();
  const [loading, setLoading] = useState<{ [key: string]: boolean }>({});
  const [showDocs, setShowDocs] = useState(false);
  const [onboardingStores, setOnboardingStores] = useState<Array<{ storeId: string; name: string }>>([]);
  const [onboardingOpen, setOnboardingOpen] = useState(false);

  // ---- Interval Vend sync schedule -------------------------------------
  // The poll's cadence lives in the DB (vend_sync_settings), not in a cron
  // expression, so editing it here takes effect on the next minute tick with
  // no redeploy. `lockedUntil` in the future means a run is in flight.
  interface VendSyncSettings {
    enabled: boolean;
    vendOrg: string;
    intervalMinutes: number;
    nextRunAt: string | null;
    lockedUntil: string | null;
    lastRunAt: string | null;
    lastStatus: string | null;
    lastError: string | null;
    lastDurationMs: number | null;
  }

  const [vendSync, setVendSync] = useState<VendSyncSettings | null>(null);
  const [vendIntervalDraft, setVendIntervalDraft] = useState<string>("");

  const loadVendSync = async () => {
    try {
      const token = await getValidToken();
      const res = await fetch(`${config.API_URL}/admin/commands/vend-sync-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setVendSync(json.settings);
      setVendIntervalDraft(String(json.settings?.intervalMinutes ?? ""));
    } catch (err) {
      Logger.error("Failed to load Vend sync settings:", err);
    }
  };

  useEffect(() => {
    // Vaperite's schedule is the only thing that reads these settings
    if (!isVaperite) return undefined;
    loadVendSync();
    // Poll while the page is open so a run starting/finishing is visible
    // without a manual refresh.
    const t = setInterval(loadVendSync, 15000);
    return () => clearInterval(t);
    // isVaperite: the three sync routes share this component, so moving between them only
    // changes the variant; the poll must start (and stop) with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVaperite]);

  const patchVendSync = async (patch: Record<string, unknown>, successMessage: string) => {
    setLoading((prev) => ({ ...prev, vendSchedule: true }));
    try {
      const token = await getValidToken();
      const res = await fetch(`${config.API_URL}/admin/commands/vend-sync-settings`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || `HTTP ${res.status}`);
      setVendSync(json.settings);
      setVendIntervalDraft(String(json.settings?.intervalMinutes ?? ""));
      notify(successMessage, { type: "success" });
    } catch (err: any) {
      Logger.error("Failed to update Vend sync settings:", err);
      notify(err.message || "Failed to update Vend sync schedule", { type: "error" });
    } finally {
      setLoading((prev) => ({ ...prev, vendSchedule: false }));
    }
  };

  const fmtTime = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }) : "—";

  const vendRunning = Boolean(vendSync?.lockedUntil && new Date(vendSync.lockedUntil) > new Date());

  const executeSync = async (endpoint: string, successMessage: string, errorMessage: string, loadingKey: string, body: Record<string, unknown> = {}) => {
    setLoading((prev) => ({ ...prev, [loadingKey]: true }));
    const token = await getValidToken();
    try {
      const response = await fetch(`${config.API_URL}${endpoint}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        // Surface the API's reason (a 403, or 409 while another sync runs) instead of a bare status
        const body = await response.json().catch(() => ({} as Record<string, unknown>));
        const reason = typeof body.message === 'string' ? body.message : typeof body.error === 'string' ? body.error : '';
        throw new Error(reason || `HTTP ${response.status}`);
      }
      notify(successMessage, { type: "success" });
    } catch (error) {
      Logger.error(`Sync error (${loadingKey}):`, error);
      const reason = error instanceof Error && !/^HTTP \d+$/.test(error.message) ? error.message : '';
      notify(reason ? `${errorMessage}: ${reason}` : errorMessage, { type: "error" });
    } finally {
      setLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleFullSync = () => executeSync("/admin/commands/sync-vend", "Full sync started - 5-15 minutes", "Failed to start full sync", "fullSync");
  // Only this page's retailer: the API pulls that org's stores, each from its own Lightspeed
  const handleStockSync = (org: Org) => executeSync("/admin/commands/aggregate-stock", "Stock sync started - ~30 seconds", "Failed to sync stock", "stockSync", { org });
  // Stores sync reads the response body: stores with no address yet open the setup dialog.
  const handleStoresSync = async () => {
    setLoading((prev) => ({ ...prev, storesSync: true }));
    try {
      const token = await getValidToken();
      const response = await fetch(`${config.API_URL}/admin/commands/sync-stores`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const body = await response.json().catch(() => ({}));
      notify("Stores sync started - ~20 seconds", { type: "success" });
      const pending = Array.isArray(body?.needsOnboarding) ? body.needsOnboarding : [];
      if (pending.length > 0) {
        setOnboardingStores(pending);
        setOnboardingOpen(true);
      }
    } catch (error) {
      Logger.error("Sync error (storesSync):", error);
      notify("Failed to sync stores", { type: "error" });
    } finally {
      setLoading((prev) => ({ ...prev, storesSync: false }));
    }
  };
  const handleRefreshViews = () => executeSync("/admin/commands/refresh-views", "Materialized views refreshed", "Failed to refresh views", "refreshViews");

  // Light bullet points style
  const bulletStyle = { py: 0, minHeight: "auto" };
  const bulletTextStyle = { m: 0, "& .MuiListItemText-primary": { fontSize: "0.875rem", color: "text.secondary" } };

  // Documentation content
  const DocsPanel = () => (
    <Paper sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h5">Sync Operations Guide</Typography>
        <Button size="small" onClick={() => setShowDocs(false)} startIcon={<CloseIcon />}>
          Close
        </Button>
      </Box>

      {isDeeliver && (
      <>
      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Daily Schedule</Typography>
      <Typography variant="body2" paragraph>
        <strong>2:00 AM</strong> - Full Inventory sync from Deeliver's Lightspeed
      </Typography>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" sx={{ mb: 1 }}>Full Inventory Sync - Deeliver</Typography>
      <Typography variant="body2" paragraph>
        Imports ALL products, brands, categories, and stock from the Lightspeed API.
        Takes 5-15 minutes depending on inventory size. Avoid running during peak hours
        as it can impact API performance.
      </Typography>
      <Typography variant="body2" paragraph>
        <strong>What it syncs:</strong> Products, categories, brands, barcodes, prices,
        descriptions, images, product flags (isNew, isPopular), and stock levels for every store.
      </Typography>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" sx={{ mb: 1 }}>Stock Sync - Deeliver</Typography>
      <Typography variant="body2" paragraph>
        Pulls live stock from Deeliver's Lightspeed for Deeliver's stores and updates the per-store
        quantities, then refreshes the stock views. Takes ~1-2 minutes. Safe to run anytime - it only
        touches quantities, never product metadata, and stages nothing.
      </Typography>
      <Typography variant="body2" paragraph>
        <strong>Scope:</strong> Deeliver's stores only (Vaperite has its own on Vaperite Sync). Use this
        when stock looks wrong but the catalog is fine — it is the fast half of the Full Inventory
        sync, without the product, category and image passes.
      </Typography>
      <Typography variant="body2" paragraph>
        <strong>Never invents a zero:</strong> an outlet missing entirely from the Lightspeed
        response is skipped rather than zeroed, and a product Lightspeed does not mention is left
        as-is. Quantities that ARE reported are applied in full, including genuine zeros.
      </Typography>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" sx={{ mb: 1 }}>Real-Time Updates</Typography>
      <Typography variant="body2" paragraph>
        Stock and product edits in Deeliver's Lightspeed also arrive in real time via webhooks,
        in between the 2AM runs.
      </Typography>
      </>
      )}

      {showAll && (
      <>
      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Stores from Radar</Typography>
      <Typography variant="body2" paragraph>
        Creates new stores from Radar geofences. Matches Radar geofence names with
        Lightspeed outlets. Takes ~20 seconds. Existing stores are not modified.
      </Typography>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" sx={{ mb: 1 }}>Refresh Views - Entire App</Typography>
      <Typography variant="body2" paragraph>
        Rebuilds the pre-computed database views that track which products and categories
        are available at each store. Makes no API calls — it only re-processes
        existing database data. Takes ~5-15 seconds.
      </Typography>
      <Typography variant="body2" paragraph>
        <strong>When to use:</strong> If a category is missing from the mobile app but shows products
        in the admin panel "eye" preview, or if product availability seems out of sync after
        product-category assignments were changed. Covers every store, Deeliver and Vaperite.
      </Typography>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" sx={{ mb: 1 }}>Where Stock Comes From</Typography>
      <Typography variant="body2" paragraph>
        Stock is Lightspeed-owned across every store: Deeliver's syncs are on Deeliver Sync,
        Vaperite's on Vaperite Sync. The app itself reads stock from materialized views, so a
        store can hold correct stock in the database and still show nothing until those views
        are refreshed.
      </Typography>
      </>
      )}
    </Paper>
  );

  return (
    <Box sx={{ display: "flex", height: "calc(100vh - 64px)" }}>
      <StoreOnboardingDialog stores={onboardingStores} open={onboardingOpen} onClose={() => setOnboardingOpen(false)} />
      {/* Main Controls Panel */}
      <Box sx={{ flex: 1, p: 3, overflow: "auto" }}>
        {/* Header with Docs button */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Title title={isDeeliver ? "Deeliver Sync" : isVaperite ? "Vaperite Sync" : "Sync Control"} />
          {/* The guide covers this page's own cards; Vaperite's page has none. */}
          {showDocsButton && (
            <Button
              variant={showDocs ? "contained" : "outlined"}
              startIcon={<DocsIcon />}
              onClick={() => setShowDocs(!showDocs)}
              size="small"
            >
              {showDocs ? "Hide Docs" : "Documentation"}
            </Button>
          )}
        </Box>



        <Grid container spacing={3}>
          {/* Vaperite's scheduled sync — lives on the Vaperite page only; interval and
              on/off are in the DB (vend_sync_settings 'vaperite-vend'), editable here. */}
          {isVaperite && (
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
                  <ScheduleIcon sx={{ color: "primary.main" }} />
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>Scheduled Vaperite Sync</Typography>
                  {vendRunning && <Chip label="Running now" size="small" color="info" />}
                  <Chip
                    label={vendSync?.enabled ? "Enabled" : "Disabled"}
                    size="small"
                    color={vendSync?.enabled ? "success" : "default"}
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Pulls Vaperite's Lightspeed catalogue and stock on a timer into Vaperite Item
                  Staging for review. Settings take effect without a redeploy. <strong>Run now</strong>{" "}
                  runs it once even while the schedule is switched off.
                </Typography>

                <Grid container spacing={2} alignItems="center">
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(vendSync?.enabled)}
                          disabled={!vendSync || loading.vendSchedule}
                          onChange={(e) => patchVendSync(
                            { enabled: e.target.checked },
                            e.target.checked
                              ? "Scheduled sync enabled — first run at the time shown below"
                              : "Scheduled sync disabled",
                          )}
                        />
                      }
                      label="Enabled"
                    />
                  </Grid>

                  {/* The account selector is gone. This poller is Vaperite's, and
                      Deeliver already syncs on the 2AM cron — pointing this at
                      Deeliver gave that catalogue two writers. The org is shown
                      below rather than chosen. */}
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      label="Every (minutes)"
                      type="number"
                      size="small"
                      fullWidth
                      value={vendIntervalDraft}
                      disabled={!vendSync || loading.vendSchedule}
                      onChange={(e) => setVendIntervalDraft(e.target.value)}
                      inputProps={{ min: 1, max: 10080 }}
                      helperText="1 to 10080 (7 days)"
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      disabled={
                        !vendSync
                        || loading.vendSchedule
                        || vendIntervalDraft === String(vendSync?.intervalMinutes ?? "")
                      }
                      onClick={() => patchVendSync(
                        { intervalMinutes: Number(vendIntervalDraft) },
                        `Interval set to ${vendIntervalDraft} minutes — applies after the next run`,
                      )}
                    >
                      Save interval
                    </Button>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={loading.vendSchedule ? <CircularProgress size={18} /> : <RunIcon />}
                      disabled={!vendSync || loading.vendSchedule || vendRunning}
                      onClick={() => patchVendSync(
                        { runNow: true },
                        vendSync?.enabled ? "Queued — starts within a minute" : "Run started",
                      )}
                    >
                      Run now
                    </Button>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Typography variant="caption" color="text.secondary">Next run</Typography>
                    <Typography variant="body2">{fmtTime(vendSync?.nextRunAt ?? null)}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Typography variant="caption" color="text.secondary">Last run</Typography>
                    <Typography variant="body2">{fmtTime(vendSync?.lastRunAt ?? null)}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Typography variant="caption" color="text.secondary">Last result</Typography>
                    <Typography
                      variant="body2"
                      color={vendSync?.lastStatus === "error" ? "error.main" : "text.primary"}
                    >
                      {vendSync?.lastStatus ?? "—"}
                      {vendSync?.lastDurationMs != null && ` (${Math.round(vendSync.lastDurationMs / 1000)}s)`}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Typography variant="caption" color="text.secondary">Pulling from</Typography>
                    <Typography variant="body2">Vaperite Lightspeed → Item Staging (review)</Typography>
                  </Grid>
                </Grid>

                {vendSync?.lastStatus === "error" && vendSync?.lastError && (
                  <Alert severity="error" sx={{ mt: 2 }}>{vendSync.lastError}</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
          )}

          {isDeeliver && (
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ pb: "16px !important" }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <InventoryIcon sx={{ mr: 1, color: "primary.main" }} />
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>Full Inventory Sync - Deeliver</Typography>
                  <Chip label="2AM" size="small" icon={<ScheduleIcon />} />
                </Box>
                <List dense sx={{ mb: 1 }}>
                  <ListItem sx={bulletStyle}><ListItemText sx={bulletTextStyle} primary="• 5-15 min duration" /></ListItem>
                  <ListItem sx={bulletStyle}><ListItemText sx={bulletTextStyle} primary="• Syncs products, categories, brands, stock from Deeliver's Lightspeed" /></ListItem>
                  <ListItem sx={bulletStyle}><ListItemText sx={bulletTextStyle} primary="• Avoid peak hours" /></ListItem>
                </List>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={loading.fullSync ? <CircularProgress size={20} /> : <SyncIcon />}
                  onClick={handleFullSync}
                  disabled={loading.fullSync}
                  fullWidth
                >
                  {loading.fullSync ? "Running..." : "Run Full Sync"}
                </Button>
              </CardContent>
            </Card>
          </Grid>
          )}

          {/* Stock Sync — one per retailer, on that retailer's page */}
          {!showAll && (
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ pb: "16px !important" }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <StockIcon sx={{ mr: 1, color: "success.main" }} />
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>Stock Sync - {isDeeliver ? "Deeliver" : "Vaperite"}</Typography>
                </Box>
                <List dense sx={{ mb: 1 }}>
                  <ListItem sx={bulletStyle}><ListItemText sx={bulletTextStyle} primary="• ~1-2 min duration" /></ListItem>
                  <ListItem sx={bulletStyle}><ListItemText sx={bulletTextStyle} primary={`• Pulls live stock from ${isDeeliver ? "Deeliver's" : "Vaperite's"} Lightspeed — ${isDeeliver ? "Deeliver's" : "Vaperite's"} stores only`} /></ListItem>
                  <ListItem sx={bulletStyle}><ListItemText sx={bulletTextStyle} primary="• Stock only, no catalog changes — safe anytime" /></ListItem>
                </List>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={loading.stockSync ? <CircularProgress size={20} /> : <StockIcon />}
                  onClick={() => handleStockSync(isDeeliver ? "deeliver" : "vaperite")}
                  disabled={loading.stockSync}
                  fullWidth
                >
                  {loading.stockSync ? "Syncing..." : "Sync Stock"}
                </Button>
              </CardContent>
            </Card>
          </Grid>
          )}

          {/* App-wide cards: Sync Control only */}
          {showAll && (
          <>
          {/* Stores Sync */}
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ pb: "16px !important" }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <StoreIcon sx={{ mr: 1, color: "secondary.main" }} />
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>Stores from Radar</Typography>
                </Box>
                <List dense sx={{ mb: 1 }}>
                  <ListItem sx={bulletStyle}><ListItemText sx={bulletTextStyle} primary="• ~20 sec duration" /></ListItem>
                  <ListItem sx={bulletStyle}><ListItemText sx={bulletTextStyle} primary="• Creates new stores from Radar geofences" /></ListItem>
                  <ListItem sx={bulletStyle}><ListItemText sx={bulletTextStyle} primary="• Existing stores unchanged" /></ListItem>
                </List>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={loading.storesSync ? <CircularProgress size={20} /> : <StoreIcon />}
                  onClick={handleStoresSync}
                  disabled={loading.storesSync}
                  fullWidth
                >
                  {loading.storesSync ? "Syncing..." : "Sync Stores"}
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Refresh Materialized Views */}
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ pb: "16px !important" }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <RefreshIcon sx={{ mr: 1, color: "info.main" }} />
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>Refresh Views - Entire App</Typography>
                </Box>
                <List dense sx={{ mb: 1 }}>
                  <ListItem sx={bulletStyle}><ListItemText sx={bulletTextStyle} primary="• ~5-15 sec duration" /></ListItem>
                  <ListItem sx={bulletStyle}><ListItemText sx={bulletTextStyle} primary="• Rebuilds product stock + category availability views" /></ListItem>
                  <ListItem sx={bulletStyle}><ListItemText sx={bulletTextStyle} primary="• No API calls — uses existing DB data" /></ListItem>
                </List>
                <Button
                  variant="contained"
                  color="info"
                  startIcon={loading.refreshViews ? <CircularProgress size={20} /> : <RefreshIcon />}
                  onClick={handleRefreshViews}
                  disabled={loading.refreshViews}
                  fullWidth
                >
                  {loading.refreshViews ? "Refreshing..." : "Refresh Views"}
                </Button>
              </CardContent>
            </Card>
          </Grid>
          </>
          )}
        </Grid>


      </Box>

      {/* Documentation Panel */}
      {showDocsButton && showDocs && (
        <Box sx={{ flex: 1, borderLeft: 1, borderColor: "divider", overflow: "hidden" }}>
          <DocsPanel />
        </Box>
      )}
    </Box>
  );
};

export default SyncControl;
